import {
  ALLOTTED_SPACE_PRECISION as STORAGE_SPACE_PRECISION,
  MS_UNIT,
  OPERATIONS_COUNT_PRECISION,
  PERCENTAGE_PRECISION,
} from "../constants/common";
import { DateTime, DateTimeFormatOptions } from "luxon";
import _ from "underscore";

const UNIT_NAMES = Object.keys(MS_UNIT);

export class Formatters {
  static formatNumberUnit(
    value: number,
    customFormatter?: ((value: number) => string | number) | number,
  ) {
    if (customFormatter === undefined || typeof customFormatter === "number") {
      const precision = customFormatter ? (customFormatter as number) : 0;
      customFormatter = (v) => this.formatNumber(v, precision);
    }
    const unitNameIdx = UNIT_NAMES.findIndex((k) => value < MS_UNIT[k]),
      unitName =
        unitNameIdx === 0
          ? ""
          : UNIT_NAMES[
              unitNameIdx < 0 ? UNIT_NAMES.length - 1 : unitNameIdx - 1
            ],
      converted = unitName ? value / MS_UNIT[unitName] : value,
      formattedValue = customFormatter ? customFormatter(converted) : converted;
    return formattedValue + unitName;
  }

  static formatOperationsCount(count: number): string {
    if (count == null) {
      return "";
    }
    if (count < 10000) {
      return `${count}`;
    }
    return this.formatNumberUnit(count, OPERATIONS_COUNT_PRECISION);
  }

  static formatNetworkSpeed(bytesPerSecond: number): string {
    return `${Formatters.formatStorageSpace(bytesPerSecond)}/s`;
  }

  static formatNetworkSpeedKb(kbPerSecond: number): string {
    return Formatters.formatNetworkSpeed(kbPerSecond * 1000);
  }

  static formatStorageSpaceMb(sizeInMb?: number): string {
    return Formatters.formatStorageSpace(
      sizeInMb == null ? null : sizeInMb * MS_UNIT.M,
    );
  }

  static formatStorageSpace(
    sizeInBytes?: number,
    precision: number = STORAGE_SPACE_PRECISION,
  ): string {
    if (sizeInBytes == null) {
      return "";
    }
    return (
      this.formatNumberUnit(
        sizeInBytes,
        sizeInBytes < MS_UNIT.K ? 0 : precision,
      ) + "B"
    );
  }

  static formatPercentage(percentage: number): string {
    return `${Formatters.round(percentage || 0, PERCENTAGE_PRECISION)}%`;
  }

  static formatNumber(value: number, precision: number = 0): string {
    return new Intl.NumberFormat("en-US", {
      maximumFractionDigits: precision,
    }).format(value);
  }

  static round(value: number, precision: number = 0): number {
    const multiplier = Math.pow(10, precision);
    return Math.round(value * multiplier) / multiplier;
  }

  static calculateUsagePercentage(
    storageUsageMB: number,
    totalStorageMB: number,
  ): number {
    let usageInBytes = storageUsageMB * MS_UNIT.M;
    let totalInBytes = totalStorageMB * MS_UNIT.M;
    let percentage = (usageInBytes / totalInBytes) * 100;
    return isNaN(percentage) ? 0 : parseInt(percentage.toFixed(2));
  }

  static formatDate(
    date: string | Date | DateTime,
    format?: DateTimeFormatOptions,
  ): string {
    let dateTime: DateTime = date as DateTime;
    if (date) {
      if (_.isString(date)) {
        dateTime = DateTime.fromISO(date);
      } else if (_.isDate(date)) {
        dateTime = DateTime.fromJSDate(date);
      }
      return dateTime.toLocaleString(format || DateTime.DATE_SHORT);
    }
    return "";
  }

  static formatDateTime(
    date: string | Date | DateTime,
    format?: DateTimeFormatOptions,
  ): string {
    return this.formatDate(date, format || DateTime.DATETIME_SHORT);
  }

  static formatTime(
    date: string | Date | DateTime,
    format?: DateTimeFormatOptions,
  ): string {
    return this.formatDate(date, format || DateTime.TIME_SIMPLE);
  }

  static formatBuildTimestamp(
    timestamp: string,
    format?: DateTimeFormatOptions,
  ): string {
    const dateTime = DateTime.fromFormat(timestamp, "yyyyMMddHHmmssZZZ", {
      setZone: true,
    });

    if (!dateTime.isValid) {
      return timestamp;
    }

    return this.formatDateTime(dateTime, format);
  }
}
